import { AxiosInstance } from "axios";
import { TagProps } from "@/components-v2/Tag";
import {
  AccountCompanyDetails,
  accountFeatureStatusLabels,
  AccountSubscriptionStats,
  BillingFrequency,
  billingFrequencyLabels,
  CompanyItem,
  EnterpriseAccountDetails,
  EnterpriseAccountFeature,
  EnterpriseAccountFeatureStatus,
  EnterpriseAccountFeatureType,
  EnterpriseAccountStatsDetails,
  EnterpriseAccountUser,
  failure,
  isLegacyProduct,
  isOk,
  ok,
  pending,
  Portfolio,
  ProductName,
  productNames,
  Result,
  SsoProviderSettings,
  UserItem,
} from "@/common/common";
import { makeAutoObservable, observable } from "mobx";
import {
  BooleanFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import { isBlank, isNotBlank, removeAllWhitespace } from "@/utils/strings";
import { TMessageKey } from "@/i18n/types.gen";
import { TabProps } from "@/components-v2/Tabs";
import { newUuid } from "@/utils/uuid";
import { TMessage, TMessageParam } from "@/i18n/tt";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { Language, toSupportedLanguage } from "@/i18n/languages";
import { formatNumber } from "@/utils/numbers";
import {
  confirmAction,
  DCEvent,
  EventDispatcher,
  loadUrl,
  navigate,
  showAlert,
  thunk,
} from "@/events/eventsDomain";
import { adminOverview, superadminSearchUrl, superadminUrl } from "@/utils/navigation";
import { SharepointConnectionResult, SharepointSettings } from "@/sharepoint/sharepointDomain";
import { UserAccess, UserByEmailOrSocialSecurityNumber } from "@/AdminApp/AdminAppController";
import { NamedOrganisationNumber } from "@/types/Transactions";
import { compact, identity, sortBy, uniq, uniqBy } from "lodash";
import UserStore from "@/stores/UserStore";
import { User } from "@/types/UserInfo";
import { formatName } from "@/types/Shareholder";
import type { AdminOption, ParentOption } from "@/addCompany/addCompanyDomain";
import { AccessType } from "@/types/enums/AccessType";
import { MenuLinkProps } from "@/components-v2/MenuBar";
import { SideMenuEntry } from "@/components-v2/sideMenuDomain";
import { IconName } from "@/components-v2/iconMapping";
import { TextWithTooltipProps } from "@/components-v2/TextWithTooltip";
import { DropdownMenuProps } from "@/components-v2/Dropdown.tsx";
import { InviteNewAdminDialogStore } from "@/portfolio/contacts/contactsDomain.ts";
import { portfoliosOfCompanies, SelectableCompany } from "@/portfolio/portfolioDomain";
import {
  getRoleNameByLanguage,
  InviteUserToCustomRoleModel,
} from "@/portfolio/contacts/customRoleDomain.ts";
import { CustomRoleItem } from "@/common/customRole.ts";
import { DataCardProps } from "@/components-v2/DataCard.tsx";
import { ProductDescription } from "@/common/product";
import { registerSubscriptions } from "@/companyRegistry/companyRegistryApiClient";
import { GridItemProps, GridProps } from "@/components-v2/Box.tsx";
import { when } from "@/utils/functions.ts";

type SsoType = "google" | "microsoft";

const domainPattern = /^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

const validateDomain = (value: string): TMessage | undefined => {
  if (!domainPattern.test(value)) {
    return { key: "sso.invalid-domain" };
  }
};

type BillingFrequencyInformation = {
  id: string;
  cycleType: BillingFrequency;
  endDate: string;
  totalActiveCompanies: number;
};
export const calcColCount = (row: BillingFrequencyCards[], max: number): GridProps["cols"] =>
  Math.min(
    row.reduce((acc, card) => acc + (card.colSpan === "full" ? 12 : (card.colSpan ?? 1)), 0),
    max,
  ) as GridProps["cols"];

export type BillingFrequencyCards = {
  colSpan?: GridItemProps["colSpan"];
  diffTag?: TagProps;
} & DataCardProps<BillingFrequencyInformation>;

const norwegianAksjeselskap = "NO_AS";

const norwegianAsOnlyProducts: ReadonlySet<string> = new Set<ProductName>([
  "digital_company",
  "enterprise",
]);

export function findProductId(
  products: ProductDescription[],
  productName: ProductName,
): string | undefined {
  return products.find((p) => p.productName === productName)?.productId;
}

export function matchesCompanySearch(company: AccountCompanyDetails, search: string): boolean {
  const query = search.toLowerCase();
  const orgNoQuery = query.replace(/[\s.-]/g, "");
  return (
    company.companyName.toLowerCase().includes(query) ||
    (orgNoQuery !== "" && company.orgNo.toLowerCase().includes(orgNoQuery))
  );
}

export function offerableProducts(products: ProductDescription[]): ProductDescription[] {
  return products.filter((p) => !isLegacyProduct(p.productName));
}

export function subscriptionRestriction(
  products: ProductDescription[],
  productId: string | null,
  companies: AccountCompanyDetails[],
): TMessage | undefined {
  const productName = products.find((p) => p.productId === productId)?.productName;
  if (productName === undefined || !norwegianAsOnlyProducts.has(productName)) return;
  if (companies.every((c) => c.organisationType === norwegianAksjeselskap)) return;
  return { key: "enterprise-account.subscription.norwegian-as-only" };
}

const registerProductNames: ReadonlySet<string> = new Set(registerSubscriptions);

export function isDowngradeToOwnershipRegister(
  fromProductName: string | undefined,
  toProductName: string | undefined,
): boolean {
  return (
    toProductName === OWNERSHIP_REGISTER_PRODUCT &&
    fromProductName !== undefined &&
    !registerProductNames.has(fromProductName)
  );
}

export function downgradesToOwnershipRegister(
  products: ProductDescription[],
  companies: AccountCompanyDetails[],
  productId: string | null,
): boolean {
  const toProductName = productNameById(products, productId);
  return companies.some((c) =>
    isDowngradeToOwnershipRegister(productNameById(products, c.productId), toProductName),
  );
}

function productNameById(
  products: ProductDescription[],
  productId: string | null | undefined,
): string | undefined {
  return products.find((p) => p.productId === productId)?.productName;
}

export const confirmDowngradeToOwnershipRegister = (onConfirm: DCEvent[]): DCEvent[] =>
  confirmAction({
    title: { key: "enterprise-account.downgrade-to-ownership-register-dialog.title" },
    text: { key: "enterprise-account.downgrade-to-ownership-register-dialog.description" },
    confirmLabel: { key: "continue" },
    onConfirm,
  });

export const openAccountCompany = (orgNo: string, hasSuperAdminAccess: boolean): DCEvent[] =>
  hasSuperAdminAccess
    ? [loadUrl(superadminSearchUrl(orgNo))]
    : [navigate(adminOverview(orgNo), { newWindow: true })];

export class EnterpriseAccountsStore {
  accountResult?: Result<EnterpriseAccountDetails>;
  nameFilter: StringFieldModel = new StringFieldModel();
  subscriptionTypeFilter?: RadioFieldModel<string>;
  portfolioFilter?: RadioFieldModel<string>;
  customRoleFilter?: RadioFieldModel<string>;
  addCompaniesModel?: AddCompaniesModel;
  submitCompaniesResult?: Result;
  submitDialogResult?: Result;
  sharepointSettingsResult?: Result<SharepointSettings | undefined>;
  activeTab: EnterpriseAccountsTab = "companies";
  integrationSettingsModel?: IntegrationSettingsModel;
  submitSharepointSettingsResult?: Result<TMessage, TMessage>;
  sharepointSetupSiteUrl?: StringFieldModel;
  sharepointConnectionTestPending = false;
  sharepointConnectionTestResult?: SharepointConnectionResult;
  submitSSOSettingsResult?: Result<TMessage, TMessage>;
  showDialog: EnterpriseAccountDialog = { type: "none" };
  id?: string;
  findCompaniesResult?: Result<RelatedCompany[]>;
  updateCompaniesTitleCaseResult?: Result;
  adminUserFilter?: RadioFieldModel<UserItem | null>;
  checkedOrgNos: Set<string> = new Set();
  updateMultipleCompaniesModel?: UpdateMultipleCompaniesModel;
  selectedCompanyDetails?: AccountCompanyDetails;
  offboardSelectedCompany = false;
  addAccountAdminIdLoading?: string;
  editFeatureModel?: EditFeatureModel;
  purgeAccountResult: Result | undefined = undefined;
  confirmPortfolioDeletionModel: BooleanFieldModel | undefined = undefined;
  accountPortfolios: Portfolio[] = [];
  statsResult?: Result<EnterpriseAccountStatsDetails>;
  usersSearchModel: StringFieldModel = new StringFieldModel();

  constructor(
    readonly http: AxiosInstance,
    readonly messageStore: AlertMessageStore,
    readonly dispatch: EventDispatcher,
    readonly userStore: UserStore,
  ) {
    makeAutoObservable(this, { http: false }, { autoBind: true });
  }

  *fetchEnterpriseAccount(id: string) {
    this.id = id;
    this.accountResult = pending();
    try {
      const response: EnterpriseAccountDetails = yield this.http
        .get(`/enterprise-accounts/${this.id}`)
        .then((response) => response.data);
      this.accountResult = ok(response);

      this.subscriptionTypeFilter = new RadioFieldModel<string>({
        value: "all",
        options: () => ["all", ...this.activeProductsForAllCompanies],
        idFn: (s) => s,
        labelFn: (s) => ({ translated: s }),
      });

      const { customRoleItems, users } = response;
      const rolesWithUsers = customRoleItems.filter((c) => c.availableUsers.length > 0);
      const hasAdmins = users.some((u) => u.role.type === "admin");
      if (rolesWithUsers.length > 0 && hasAdmins) {
        const customRoleOptions = rolesWithUsers.map((c) =>
          this.customRoleName(c.role.customRoleId, c.role.customRoleName),
        );
        const allOptions = ["allRoles", "ADMIN", ...customRoleOptions];
        const previousValue = this.customRoleFilter?.value;
        const preservedValue =
          previousValue && allOptions.includes(previousValue) ? previousValue : "allRoles";
        this.customRoleFilter = new RadioFieldModel<string>({
          value: preservedValue,
          options: allOptions,
          idFn: (item) => item,
          labelFn: (item) => {
            if (item === "allRoles") return { key: "roles.all" };
            if (item === "ADMIN") return { key: "administrators" };
            return { translated: item };
          },
        });
      } else {
        this.customRoleFilter = undefined;
      }

      const { companyDetails } = response;
      const uniquePortfolios = [
        ...new Set(companyDetails.map((f) => f.portfolioId).filter(isNotBlank)),
      ];
      this.portfolioFilter =
        uniquePortfolios.length > 0
          ? new RadioFieldModel<string>({
              value: "all-portfolios",
              options: ["all-portfolios", ...uniquePortfolios],
              labelFn: (id) => {
                if (id === "all-portfolios") return { key: "all-portfolios" };
                const name = companyDetails.find((f) => f.portfolioId === id)?.portfolioName;
                return { translated: name ?? "" };
              },
              idFn: (id) => id,
            })
          : undefined;
    } catch (e) {
      this.accountResult = failure();
    }
  }

  get productIds() {
    return this.products.map((p) => p.productId);
  }

  get products(): ProductDescription[] {
    return isOk(this.accountResult) ? offerableProducts(this.accountResult.data.products) : [];
  }

  billingFrequencyExtraInfoTag(productName: string, currentCount: number): TagProps {
    const previous = isOk(this.accountResult)
      ? this.accountResult.data.previousInvoiceCounts?.[productName]
      : undefined;
    if (previous === undefined) return { color: "neutral", label: "0" };
    const diff = currentCount - previous;
    return {
      color: diff > 0 ? "success" : diff < 0 ? "error" : "neutral",
      label: diff > 0 ? `+${diff}` : `${diff}`,
    };
  }

  get billingFrequencyCards(): BillingFrequencyCards[][] {
    if (!isOk(this.accountResult)) return [];
    const { products, companyDetails } = this.accountResult.data;
    const digitalCompanyId = findProductId(products, "digital_company");
    const shareholderRegisterId = findProductId(products, "shareholder_register");

    const digitalCount = companyDetails.filter((c) => c.productId === digitalCompanyId).length;
    const shareholderCount = companyDetails.filter(
      (c) => c.productId === shareholderRegisterId,
    ).length;

    return [
      [
        {
          title: { key: "enterprise-account.next-billing-date" },
          value: this.nextBillingDate,
          extraInfo: this.billingFrequency,
        },
        {
          title: { key: "enterprise-account.total-active-companies" },
          value: { type: "number", value: companyDetails.length },
          diffTag: this.billingFrequencyExtraInfoTag("", companyDetails.length),
        },
        {
          title: { key: "enterprise-account.digital-company" },
          value: { type: "number", value: digitalCount },
          diffTag: this.billingFrequencyExtraInfoTag("digital_company", digitalCount),
        },
        {
          title: { key: "enterprise-account.shareholder-register" },
          value: { type: "number", value: shareholderCount },
          diffTag: this.billingFrequencyExtraInfoTag("shareholder_register", shareholderCount),
        },
      ],
    ];
  }

  titleAddCompanies(u: EnterpriseAccountUser): TMessage {
    if (u.role.type === "admin") {
      return { key: "enterprise-account.add-access.title.admin", params: { name: u.name } };
    }
    return {
      key: "enterprise-account.add-access.title.custom-role",
      params: { name: u.name, roleName: this.customRoleName(u.role.roleId, u.role.roleName) },
    };
  }

  titleRemoveCompanies(u: EnterpriseAccountUser): TMessage {
    if (u.role.type === "admin") {
      return { key: "enterprise-account.remove-admin-access.title", params: { name: u.name } };
    }
    return {
      key: "enterprise-account.remove-custom-role-access.title",
      params: { name: u.name, roleName: this.customRoleName(u.role.roleId, u.role.roleName) },
    };
  }

  getProductLanguage(id: string | null, lang: Language) {
    if (isOk(this.accountResult)) {
      return this.accountResult.data.products
        .find((p) => p.productId === id)
        ?.productLanguages.find((l) => l.language === lang);
    }
  }

  reset() {
    this.accountResult = undefined;
    this.subscriptionTypeFilter = undefined;
    this.portfolioFilter = undefined;
    this.customRoleFilter = undefined;
  }

  *refresh() {
    const accountId = this.id;
    if (accountId) {
      yield this.fetchEnterpriseAccount(accountId);
    }
  }

  get companyData() {
    return isOk(this.accountResult) ? this.filter(this.accountResult.data) : [];
  }

  get hasSuperAdminAccess(): boolean {
    return this.userStore.hasSuperadminAccess;
  }

  get currentUserExternalId(): string | undefined {
    return this.userStore.user?.externalId;
  }

  get isAccountAdmin(): boolean {
    const externalId = this.currentUserExternalId;
    return (
      !!externalId &&
      isOk(this.accountResult) &&
      this.accountResult.data.account.accountAdminIds.includes(externalId)
    );
  }

  get canAddCompany(): boolean {
    return this.hasSuperAdminAccess || this.isAccountAdmin;
  }

  get canInviteUsers(): boolean {
    return this.hasSuperAdminAccess || this.isAccountAdmin;
  }

  get canUpdateSubscriptions(): boolean {
    return (
      this.hasSuperAdminAccess ||
      (this.isAccountAdmin &&
        isOk(this.accountResult) &&
        this.accountResult.data.account.accountAdminsCanUpdateSubscriptions)
    );
  }

  get addCompanyAdminOptions(): AdminOption[] {
    const accountResult = this.accountResult;
    if (!isOk(accountResult)) return [];
    return accountResult.data.users
      .filter((u) => u.role.type === "admin")
      .map((u) => ({
        id: u.id,
        name: u.name ?? "",
        isAccountAdmin: accountResult.data.account.accountAdminIds.includes(u.id),
      }));
  }

  get addCompanyParentOptions(): ParentOption[] {
    return isOk(this.accountResult)
      ? this.accountResult.data.companyDetails
          .map((company) => ({ orgNo: company.orgNo, name: company.companyName }))
          .sort((a, b) => a.name.localeCompare(b.name))
      : [];
  }

  addCompanySubscriptionOptions(lang: Language): AccountSubscription[] {
    return isOk(this.accountResult) ? subscriptionsWithQuota(this.accountResult.data, lang) : [];
  }

  addCompanyRegisters(lang: Language): AccountSubscription[] {
    return registerAccountSubscriptions(
      isOk(this.accountResult) ? this.accountResult.data.products : [],
      lang,
    );
  }

  get accountName(): string {
    return isOk(this.accountResult) ? this.accountResult.data.account.name : "";
  }

  get confirmPortfolioDeletionLabel(): TMessage {
    return {
      key: "enterprise-account.confirm-delete-portfolio",
      params: {
        accountName: isOk(this.accountResult) ? this.accountResult.data.account.name : "",
        portfolioName: this.accountPortfolios.map((p) => p.name).join(", "),
      },
    };
  }

  get activeProductsForAllCompanies(): string[] {
    if (!isOk(this.accountResult)) return [];
    return [
      ...new Set(
        compact(this.accountResult.data.companyDetails.map((company) => company.productId)),
      ),
    ];
  }

  get nextBillingDate(): TMessage | TMessageParam {
    if (!isOk(this.accountResult)) return { translated: "" };
    return (
      when(
        this.accountResult.data.nextBillingDate,
        (value) => ({ type: "date", value }) as const,
      ) ?? { translated: "" }
    );
  }

  get billingFrequency(): TMessage {
    if (!isOk(this.accountResult)) return { translated: "" };
    const { billingFrequency } = this.accountResult.data.account;
    if (!billingFrequency) return { translated: "" };
    return { key: billingFrequencyLabels[billingFrequency] };
  }

  get filterUsers(): EnterpriseAccountUser[] {
    if (!isOk(this.accountResult)) return [];
    const searchString = this.usersSearchModel.inputValue.toLowerCase();
    const selectedRole = this.customRoleFilter?.value;
    return this.accountResult.data.users.filter(
      (u) => this.matchesRoleFilter(u, selectedRole) && this.matchesSearchFilter(u, searchString),
    );
  }

  matchCompanies(item: EnterpriseAccountUser, query: string): boolean {
    return item.companies.some(
      (c) =>
        c.companyName.toLowerCase().includes(query) ||
        c.organisationNumber.toLowerCase().includes(query),
    );
  }

  matchesRoleFilter(u: EnterpriseAccountUser, selectedRole: string | undefined): boolean {
    if (!selectedRole || selectedRole === "allRoles") return true;
    if (selectedRole === "ADMIN") return u.role.type === "admin";
    return (
      u.role.type === "customRole" &&
      this.customRoleName(u.role.roleId, u.role.roleName) === selectedRole
    );
  }

  matchesSearchFilter(u: EnterpriseAccountUser, searchString: string): boolean {
    if (!searchString) return true;
    const roleText =
      u.role.type === "admin"
        ? "administrator"
        : this.customRoleName(u.role.roleId, u.role.roleName).toLowerCase();
    return (
      u.name.toLowerCase().includes(searchString) ||
      u.email.toLowerCase().includes(searchString) ||
      roleText.includes(searchString) ||
      this.matchCompanies(u, searchString)
    );
  }

  private get language(): Language {
    return toSupportedLanguage(this.userStore.user?.language);
  }

  private filter(data: EnterpriseAccountDetails) {
    const searchString = this.nameFilter.inputValue;
    const details = searchString
      ? data.companyDetails.filter((c) => matchesCompanySearch(c, searchString))
      : data.companyDetails;

    const subscriptionType = this.subscriptionTypeFilter?.value;
    const filtered =
      subscriptionType && subscriptionType !== "all"
        ? details.filter((d) => d.productId === subscriptionType)
        : details;
    const portfolio = this.portfolioFilter?.value;
    if (!portfolio || portfolio === "all-portfolios") {
      return filtered;
    }
    return filtered.filter((d) => d.portfolioId === portfolio);
  }

  accessRequestRecipientLabel(item: AccessRequestRecipients): TextWithTooltipProps {
    switch (item) {
      case AccessRequestRecipients.ALL_ADMINS:
        return this.allAdminsOnlyLabel();
      case AccessRequestRecipients.ACCOUNT_ADMINS:
        return this.accountAdminsOnlyLabel();
    }
  }

  dataProtectionLabel(item: NationalIdMaskingPolicy): TextWithTooltipProps {
    switch (item) {
      case NationalIdMaskingPolicy.FULLY_MASKED:
        return { text: { key: "enterprise-account.account-settings.data-protection.no-users" } };
      case NationalIdMaskingPolicy.ACCOUNT_ADMINS:
        return this.accountAdminsOnlyLabel();
      case NationalIdMaskingPolicy.ALL_ADMINS:
        return this.allAdminsOnlyLabel();
    }
  }

  private allAdminsOnlyLabel(): TextWithTooltipProps {
    const items = isOk(this.accountResult)
      ? this.accountResult.data.users.filter((u) => u.role.type === "admin")
      : [];
    if (items.length === 0)
      return { text: { key: "enterprise-account.account-settings.no-account-admins" } };
    return {
      text: {
        key: "enterprise-account.account-settings.all-admins",
        params: { count: items.length, name: items[0].name ?? "" },
      },
      tooltip: items.length > 1 ? { type: "md-list", value: items.map((u) => u.name) } : undefined,
      underline: "none",
    };
  }

  private accountAdminsOnlyLabel(): TextWithTooltipProps {
    const accountResult = this.accountResult;
    const items = isOk(accountResult)
      ? accountResult.data.users.filter(
          (u) =>
            u.role.type === "admin" && accountResult.data.account.accountAdminIds.includes(u.id),
        )
      : [];
    if (items.length === 0)
      return { text: { key: "enterprise-account.account-settings.no-account-admins" } };
    return {
      text: {
        key: "enterprise-account.account-settings.account-admins",
        params: { count: items.length, name: items[0].name ?? "" },
      },
      tooltip: items.length > 1 ? { type: "md-list", value: items.map((u) => u.name) } : undefined,
      underline: "none",
    };
  }

  get visibleAccountSettingsTabs(): AccountSettingsTab[] {
    return visibleAccountSettingsTabs({
      hasSuperAdminAccess: this.hasSuperAdminAccess,
    });
  }

  activeAccountSettingsTab(tab?: string): AccountSettingsTab {
    return activeAccountSettingsTab(tab, this.visibleAccountSettingsTabs);
  }

  accountSettingMenuProps(accountSettingsTab: AccountSettingsTab): SideMenuEntry[] {
    return accountSettingsMenuProps(this.visibleAccountSettingsTabs, accountSettingsTab, (tab) =>
      this.dispatch([navigate(`/enterprise-account/${this.id}/settings/${tab}`)]),
    );
  }

  isAccountAdminsDisabled(flag: NationalIdMaskingPolicy | AccessRequestRecipients): boolean {
    const accountResult = this.accountResult;
    return (
      (flag === NationalIdMaskingPolicy.ACCOUNT_ADMINS ||
        flag === AccessRequestRecipients.ACCOUNT_ADMINS) &&
      (!isOk(accountResult) ||
        !accountResult.data.users.some(
          (u) =>
            u.role.type === "admin" && accountResult.data.account.accountAdminIds.includes(u.id),
        ))
    );
  }

  openAddCompaniesDialog() {
    const adminUsers = isOk(this.accountResult)
      ? this.accountResult.data.users.filter((u) => u.role.type === "admin")
      : [];
    const accountAdminIds = isOk(this.accountResult)
      ? this.accountResult.data.account.accountAdminIds
      : [];
    const userOptions = adminUsers.map((u) => ({ id: u.id, name: { translated: u.name } }));
    this.addCompaniesModel = new AddCompaniesModel(
      this.http,
      userOptions,
      this.userStore.user,
      accountAdminIds,
    );
    this.addCompaniesModel.fetchPortfolios();
  }

  closeAddCompaniesDialog() {
    this.addCompaniesModel = undefined;
  }

  *setNotificationRecipients(recipients: AccessRequestRecipients) {
    try {
      yield this.http.put(`/enterprise-accounts/${this.id}/access-request-recipients`, {
        accessRequestRecipients: recipients,
      });
      if (isOk(this.accountResult)) {
        this.accountResult.data.account.accessRequestRecipients = recipients;
        this.messageStore.showGeneralSuccess();
      }
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  *setNationalIdMaskingPolicy(nationalIdMaskingPolicy: NationalIdMaskingPolicy) {
    try {
      yield this.http.put(`/enterprise-accounts/${this.id}/data-masking-policy`, {
        nationalIdMaskingPolicy: nationalIdMaskingPolicy,
      });
      if (isOk(this.accountResult)) {
        this.accountResult.data.account.nationalIdMaskingPolicy = nationalIdMaskingPolicy;
        this.messageStore.showGeneralSuccess();
      }
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  *setTRankDataSharing(trankDataSharing: boolean) {
    try {
      yield this.http.put(`/enterprise-accounts/${this.id}/trank-data-sharing`, {
        trankDataSharing,
      });
      if (isOk(this.accountResult)) {
        this.accountResult.data.account.trankDataSharing = trankDataSharing;
        this.messageStore.showGeneralSuccess();
      }
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  *setAccountAdminsCanUpdateSubscriptions(accountAdminsCanUpdateSubscriptions: boolean) {
    try {
      yield this.http.put(`/enterprise-accounts/${this.id}/account-admin-permissions`, {
        accountAdminsCanUpdateSubscriptions,
      });
      if (isOk(this.accountResult)) {
        this.accountResult.data.account.accountAdminsCanUpdateSubscriptions =
          accountAdminsCanUpdateSubscriptions;
        this.messageStore.showGeneralSuccess();
      }
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  *submitAddCompaniesDialog() {
    if (
      !this.addCompaniesModel ||
      !this.addCompaniesModel?.validate() ||
      this.addCompaniesModel.parsedOrgNos.length === 0
    ) {
      this.submitCompaniesResult = undefined;
      return;
    }

    this.submitCompaniesResult = pending();
    try {
      const portfolio = this.addCompaniesModel.portfolioModel?.value;
      const response: AddCompaniesToEnterpriseAccountResponse = yield this.http
        .put(`/enterprise-accounts/${this.id}/company`, {
          orgNos: this.addCompaniesModel.parsedOrgNos,
          subscriptionType: this.addCompaniesModel.subscriptionModel.value,
          completeOnboarding: !!this.addCompaniesModel.completeOnboarding.value,
          adminUserIds: this.addCompaniesModel.adminUsers?.selectedIds ?? [],
          portfolioId: portfolio !== NewPortfolioOption ? portfolio?.id : undefined,
        })
        .then((r) => r.data);
      this.submitCompaniesResult = ok();

      this.showSubmitCompaniesMessage(response);
      this.addCompaniesModel = undefined;
      this.refresh();
    } catch (e) {
      this.submitCompaniesResult = failure();
      this.messageStore.showGeneralError();
    }
  }

  openAddAdministratorAccessDialog(
    user: EnterpriseAccountUser,
    companyDetails: AccountCompanyDetails[],
  ) {
    this.showDialog = {
      type: "add-admin-access",
      model: new AddAdministratorAccessModel(user, companyDetails),
    };
  }

  *submitAddAdministratorAccessDialog(externalId: string, organisationNumbers: string[]) {
    this.submitDialogResult = pending();
    if (this.showDialog.type !== "add-admin-access") return;
    const { selectedAdmin } = this.showDialog.model;
    try {
      if (selectedAdmin.role.type === "admin") {
        yield this.http
          .post<void>(`/enterprise-accounts/${this.id}/grant-admin-access`, {
            externalId: externalId,
            orgNos: organisationNumbers,
          })
          .then((r) => r.data);
      } else {
        yield this.http
          .post<void>(
            `/enterprise-accounts/${selectedAdmin.id}/custom-roles/${selectedAdmin.role.roleId}/add-companies`,
            { companies: organisationNumbers },
          )
          .then((r) => r.data);
      }
      this.submitDialogResult = ok();
      this.messageStore.showGeneralSuccess();
      this.closeDialog();
      this.refresh();
    } catch (err) {
      this.submitDialogResult = failure();
      this.messageStore.showGeneralError();
    }
  }

  openGrantProjectAccessDialog(user: EnterpriseAccountUser) {
    this.showDialog = { type: "grant-project-access", user };
  }

  *submitGrantProjectAccess() {
    if (this.showDialog.type !== "grant-project-access") return;
    const { user } = this.showDialog;
    this.submitDialogResult = pending();
    try {
      const response: GrantProjectAccessResponse = yield this.http
        .post<GrantProjectAccessResponse>(`/enterprise-accounts/${this.id}/grant-project-access`, {
          externalId: user.id,
        })
        .then((r) => r.data);
      this.submitDialogResult = ok();
      this.messageStore.showSuccess({
        key: "enterprise-account.grant-project-access.success",
        params: { count: response.numberOfProjects, name: user.name },
      });
      this.closeDialog();
    } catch (err) {
      this.submitDialogResult = failure();
      this.closeDialog();
      this.messageStore.showGeneralError();
    }
  }

  openRemoveAdministratorAccessDialog(user: EnterpriseAccountUser) {
    this.showDialog = {
      type: "remove-admin-access",
      model: new RemoveAdministratorAccessModel(user),
    };
  }

  openPurgeAccountDialog() {
    this.confirmPortfolioDeletionModel = new BooleanFieldModel({ value: true });
    this.fetchAccountPortfolios();
    this.showDialog = {
      type: "purge-account",
      model: this.confirmPortfolioDeletionModel,
    };
  }

  *fetchAccountPortfolios() {
    this.accountPortfolios = yield this.http
      .get(`/enterprise-accounts/${this.id}/portfolios`)
      .then((r) => r.data);
  }

  *submitRemoveAdministratorAccessDialog(externalId: string, organisationNumbers: string[]) {
    this.submitDialogResult = pending();
    if (this.showDialog.type !== "remove-admin-access") return;
    const { selectedAdmin } = this.showDialog.model;
    try {
      if (selectedAdmin.role.type === "admin") {
        yield this.http
          .post<void>(`/enterprise-accounts/${this.id}/revoke-admin-access`, {
            externalId,
            orgNos: organisationNumbers,
          })
          .then((r) => r.data);
      } else {
        yield this.http
          .delete<void>(
            `/enterprise-accounts/${selectedAdmin.id}/custom-roles/${selectedAdmin.role.roleId}/revoke-companies`,
            { data: { companies: organisationNumbers } },
          )
          .then((r) => r.data);
      }

      this.submitDialogResult = ok();
      this.messageStore.showGeneralSuccess();
      this.closeDialog();
      this.refresh();
    } catch (err) {
      this.submitDialogResult = failure();
      this.messageStore.showGeneralError();
    }
  }

  *openFindCompaniesDialog() {
    this.checkedOrgNos.clear();
    this.showDialog = { type: "find-companies" };
    yield this.findCompanies();
  }

  *findCompanies() {
    this.findCompaniesResult = pending();
    try {
      const response: RelatedCompany[] = yield this.http
        .get(`/enterprise-accounts/${this.id}/related-companies`)
        .then((response) => response.data);
      this.findCompaniesResult = ok(response);

      const admins = uniqBy(
        response.flatMap((c) => c.admins),
        (v) => v.externalId,
      );
      this.adminUserFilter = new RadioFieldModel({
        value: null,
        options: [null, ...admins],
        idFn: (value) => (value ? value.externalId : "all"),
        labelFn: (value) =>
          value
            ? { translated: value.name }
            : { key: "enterprise-account.all-admins", params: { count: admins.length } },
      });
    } catch (e) {
      this.findCompaniesResult = failure();
    }
  }

  *updateCompaniesTitleCase() {
    this.updateCompaniesTitleCaseResult = pending();
    try {
      yield this.http
        .post(`/enterprise-accounts/${this.id}/update-companies-title-case`)
        .then((response) => response.data);
      this.updateCompaniesTitleCaseResult = ok();
      this.messageStore.showGeneralSuccess();
      if (this.id) {
        this.fetchEnterpriseAccount(this.id);
      }
    } catch (e) {
      this.updateCompaniesTitleCaseResult = failure();
      this.messageStore.showGeneralError();
    }
  }

  openUpdateMultipleCompaniesDialog() {
    this.updateMultipleCompaniesModel = isOk(this.accountResult)
      ? new UpdateMultipleCompaniesModel(this.accountResult.data)
      : undefined;

    this.showDialog = { type: "update-companies" };
  }

  get filteredCompanies() {
    if (!isOk(this.findCompaniesResult)) {
      return [];
    }
    const allCompanies = this.findCompaniesResult.data;
    const user = this.adminUserFilter?.value;
    if (user) {
      return allCompanies.filter((c) => c.admins.some((a) => a.externalId === user.externalId));
    } else {
      return allCompanies;
    }
  }

  get filteredCheckedCompanies() {
    return this.filteredCompanies.filter((c) => this.checkedOrgNos.has(c.companyData.orgNo));
  }
  setActiveTab(tab: EnterpriseAccountsTab) {
    this.activeTab = tab;
  }

  fetchDataForTab(tab: AccountSettingsTab) {
    if (tab === "integrations") this.fetchSharepointSettings();
    if (tab === "superAdmin" && this.hasSuperAdminAccess) this.fetchStats();
  }

  *fetchStats() {
    this.statsResult = pending();
    try {
      const stats: EnterpriseAccountStatsDetails = yield this.http
        .get(`/enterprise-accounts/${this.id}/stats`)
        .then((response) => response.data);
      this.statsResult = ok(stats);
    } catch (e) {
      this.statsResult = failure();
    }
  }

  get canSetupSharepoint(): boolean {
    return this.hasAccountFeature(EnterpriseAccountFeatureType.SHAREPOINT);
  }

  private hasAccountFeature(type: EnterpriseAccountFeatureType): boolean {
    return (
      isOk(this.accountResult) &&
      this.accountResult.data.account.features.some(
        (feature) =>
          feature.type === type && feature.status !== EnterpriseAccountFeatureStatus.DISABLED,
      )
    );
  }

  toggleSharepointSetupDialog() {
    this.sharepointSetupSiteUrl = this.sharepointSetupSiteUrl
      ? undefined
      : new StringFieldModel({ required: true });
  }

  sharepointSetupUrl(): string | undefined {
    const siteUrl = this.sharepointSetupSiteUrl;
    if (!this.id || !siteUrl?.validate()) return undefined;
    const returnTo = encodeURIComponent(window.location.pathname);
    return `/api/sharepoint/${this.id}/setup?siteUrl=${encodeURIComponent(siteUrl.validatedValue)}&returnTo=${returnTo}`;
  }

  *testSharepointConnection() {
    if (!this.id || this.sharepointConnectionTestPending) return;
    this.sharepointConnectionTestPending = true;
    this.sharepointConnectionTestResult = undefined;
    try {
      const result: SharepointConnectionResult = yield this.http
        .post(`/sharepoint/${this.id}/test`)
        .then((response) => response.data);
      this.sharepointConnectionTestResult = result;
      this.showSharepointConnectionResult(result);
    } catch (e) {
      this.messageStore.showError({ key: "integrations.sharepoint.test.failed" });
    } finally {
      this.sharepointConnectionTestPending = false;
    }
  }

  get isSharepointConnectionVerified(): boolean {
    return this.sharepointConnectionTestResult?.status === "CONNECTED";
  }

  private showSharepointConnectionResult(result: SharepointConnectionResult) {
    switch (result.status) {
      case "CONNECTED":
        this.messageStore.showSuccess({
          key: "integrations.sharepoint.test.connected",
          params: { siteUrl: result.siteUrl ?? "" },
        });
        return;
      case "NOT_CONFIGURED":
        this.messageStore.showError({ key: "integrations.sharepoint.test.not-configured" });
        return;
      case "NO_TOKEN":
        this.messageStore.showError({ key: "integrations.sharepoint.test.no-token" });
        return;
      case "NO_SITE_ACCESS":
        this.messageStore.showError({ key: "integrations.sharepoint.test.no-site-access" });
        return;
      case "NO_DRIVES":
        this.messageStore.showError({ key: "integrations.sharepoint.test.no-drives" });
        return;
    }
  }

  *fetchSharepointSettings() {
    if (isOk(this.sharepointSettingsResult)) return;
    this.sharepointSettingsResult = pending();
    try {
      const settings: SharepointSettings | undefined = yield this.http
        .get(`/enterprise-accounts/${this.id}/sharepoint-settings`)
        .then((response) => {
          if (response.data.id) {
            return response.data;
          } else {
            return undefined;
          }
        });
      this.sharepointSettingsResult = ok(settings);
    } catch (e) {
      this.sharepointSettingsResult = failure();
    }
  }

  get hasEntityDataFeature(): boolean {
    return this.hasAccountFeature(EnterpriseAccountFeatureType.ENTITY_DATA);
  }

  get hasTemplatesFeature(): boolean {
    return this.hasAccountFeature(EnterpriseAccountFeatureType.TEMPLATES);
  }

  get tabs(): TabProps[] {
    return visibleEnterpriseAccountsTabs({
      hasTemplates: this.hasTemplatesFeature,
      hasEntityData: this.hasEntityDataFeature,
    }).map<TabProps>((tab) => ({
      id: tab,
      isActive: this.activeTab === tab,
      label: {
        key: enterpriseAccountsTabLabels[tab],
        params: { count: this.companyData.length },
      },
      onSelect: () => {
        this.dispatch([navigate(`/enterprise-account/${this.id}/${tab}`)]);
      },
    }));
  }

  get isPurgeAccountDisabled(): boolean {
    if (this.accountPortfolios.length === 0) return false;
    return (
      this.confirmPortfolioDeletionModel === undefined ||
      this.confirmPortfolioDeletionModel.value === false
    );
  }

  get companyItems(): CompanyItem[] {
    return isOk(this.accountResult)
      ? this.accountResult.data.companyDetails.map<CompanyItem>((c) => ({
          companyName: c.companyName,
          organisationNumber: c.orgNo,
        }))
      : [];
  }

  toggleSharepointSettingsDialog() {
    if (this.integrationSettingsModel) {
      this.integrationSettingsModel = undefined;
    } else {
      if (isOk(this.sharepointSettingsResult))
        this.integrationSettingsModel = createSharepointSettingsModel(
          this.sharepointSettingsResult.data,
        );
    }
  }

  toggleSSODialog(type: SsoType) {
    if (this.integrationSettingsModel) {
      this.integrationSettingsModel = undefined;
    } else {
      const account = isOk(this.accountResult) ? this.accountResult.data.account : undefined;
      this.integrationSettingsModel = createSSOSettingsModel(
        type,
        account?.ssoSettings?.[type],
        account?.requireSsoForAllUsers,
      );
    }
  }

  *addOrUpdateSharepointSettings() {
    if (
      !(this.integrationSettingsModel?.type === "sharepoint") ||
      !this.integrationSettingsModel?.validate()
    )
      return;
    this.submitSharepointSettingsResult = pending();
    const data: SharepointSettings = {
      id: newUuid(), // backend will figure out the correct id based on the existence of sharepoint settings on the account
      siteId: this.integrationSettingsModel.siteID.validatedValue,
      applicationId: this.integrationSettingsModel.clientID.validatedValue,
      secret: this.integrationSettingsModel.clientSecret.validatedValue,
      tenantId: this.integrationSettingsModel.tenantID.validatedValue,
    };
    try {
      yield this.http.post(`/enterprise-accounts/${this.id}/sharepoint-settings`, data);
      this.submitSharepointSettingsResult = ok({ key: "general-success" });
      this.sharepointSettingsResult = undefined;
      this.fetchSharepointSettings();
      this.toggleSharepointSettingsDialog();
    } catch (e) {
      this.submitSharepointSettingsResult = failure({ key: "sharepoint.connection-failed" });
    }
  }

  *updateSSOSettings() {
    if (
      !(
        this.integrationSettingsModel?.type === "google" ||
        this.integrationSettingsModel?.type === "microsoft"
      ) ||
      !this.integrationSettingsModel?.validate()
    )
      return;
    this.submitSSOSettingsResult = pending();
    const model = this.integrationSettingsModel;
    const data = {
      type: model.type,
      domains: model.domains.map((d) => d.validatedValue).filter((domain) => !isBlank(domain)),
      requireSsoForAllUsers: model.requireSsoForAllUsers.value ?? false,
      requireBankIdOnInitialSetup: model.requireBankIdOnInitialSetup.value ?? true,
    };
    try {
      yield this.http.put(`/enterprise-accounts/${this.id}/sso-settings`, data);
      this.submitSSOSettingsResult = ok({ key: "general-success" });
      if (isOk(this.accountResult)) {
        this.accountResult.data.account.ssoSettings = {
          ...this.accountResult.data.account.ssoSettings,
          [model.type]: {
            domains: data.domains,
            requireBankIdOnInitialSetup: data.requireBankIdOnInitialSetup,
          },
        };
        this.accountResult.data.account.requireSsoForAllUsers = data.requireSsoForAllUsers;
      }
      this.toggleSSODialog(model.type);
      this.fetchEnterpriseAccount(this.id!);
    } catch (e) {
      this.submitSSOSettingsResult = failure({ key: "general-error" });
    }
  }

  *deleteSSOSettings() {
    if (
      !(
        this.integrationSettingsModel?.type === "google" ||
        this.integrationSettingsModel?.type === "microsoft"
      )
    )
      return;
    const model = this.integrationSettingsModel;
    try {
      yield this.http.delete(`/enterprise-accounts/${this.id}/sso-settings/${model.type}`);
      if (isOk(this.accountResult)) {
        const updated = { ...this.accountResult.data.account.ssoSettings };
        delete updated[model.type];
        this.accountResult.data.account.ssoSettings = updated;
      }
      this.toggleSSODialog(model.type);
      this.fetchEnterpriseAccount(this.id!);
    } catch (e) {
      this.submitSSOSettingsResult = failure({ key: "general-error" });
    }
  }

  openRemoveAllCompaniesDialog() {
    this.showDialog = { type: "remove-all-companies" };
  }

  closeDialog() {
    this.showDialog = { type: "none" };
  }

  *removeAllCompanies() {
    try {
      yield this.http.delete(`/enterprise-accounts/${this.id}/company`);

      this.closeDialog();
      this.refresh();
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  openDeleteAccountDialog() {
    this.showDialog = { type: "delete-account" };
  }

  get selectableCompanies(): SelectableCompany[] {
    return isOk(this.accountResult)
      ? this.accountResult.data.companyDetails.map((c) => ({
          organisationNumber: c.orgNo,
          name: c.companyName,
          portfolioId: c.portfolioId,
          portfolioName: c.portfolioName,
        }))
      : [];
  }

  openInviteAdminDialog() {
    this.showDialog = {
      type: "invite-admin",
      model: new InviteNewAdminDialogStore({
        locale: this.language,
        onClose: this.closeDialog,
        apiService: this.userStore.apiService,
        messageStore: this.messageStore,
        companies: this.selectableCompanies,
        portfolios: portfoliosOfCompanies(this.selectableCompanies),
      }),
    };
  }

  openInviteCustomRoleDialog({
    customRoleItem,
    locale,
  }: {
    customRoleItem: CustomRoleItem;
    locale: Language;
  }) {
    const creatorCompany = this.userStore.companies
      .filter((allCompanies) => allCompanies.accessType === AccessType.ADMIN)
      .find((adminCompany) => adminCompany.organisationNumber === customRoleItem.role.orgNo);
    this.showDialog = {
      type: "invite-custom-role",
      model: new InviteUserToCustomRoleModel({
        customRoleItem,
        availableCompanies: this.selectableCompanies,
        apiService: this.userStore.apiService,
        locale,
        dispatch: this.dispatch,
        isLoadingInvite: false,
        showEmailField: true,
        hasAdminInCreatorCompany: creatorCompany !== undefined,
        selectedUserCompanyItems: [],
        userName: "",
        closeDialog: this.closeDialog,
      }),
    };
  }

  *deleteAccount() {
    try {
      yield this.http.delete(`/enterprise-accounts/${this.id}`);

      this.closeDialog();
      this.dispatch([navigate(`${superadminUrl}/enterprise-accounts`)]);
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  changeSubscriptionForCompany(row: AccountCompanyDetails, productId: string | null) {
    if (row.productId === productId) return;
    const products = isOk(this.accountResult) ? this.accountResult.data.products : [];
    const update = [thunk(() => this.updateSubscriptionForCompany(row, productId))];
    this.dispatch(
      downgradesToOwnershipRegister(products, [row], productId)
        ? confirmDowngradeToOwnershipRegister(update)
        : update,
    );
  }

  *updateSubscriptionForCompany(row: AccountCompanyDetails, productId: string | null) {
    try {
      const stats: AccountSubscriptionStats[] = yield this.http
        .post(`/enterprise-accounts/${this.id}/company`, {
          orgNos: [row.orgNo],
          changeType: EnterpriseAccountChangeType.subscription,
          productId,
          offboard: false,
        })
        .then((r) => r.data);

      if (isOk(this.accountResult)) {
        this.accountResult.data.subscriptionStats = stats;
        const companyDetails = this.accountResult.data.companyDetails.find(
          (c) => c.orgNo === row.orgNo,
        );
        if (companyDetails) {
          if (
            downgradesToOwnershipRegister(
              this.accountResult.data.products,
              [companyDetails],
              productId,
            )
          ) {
            companyDetails.shareholderCount = 0;
          }
          companyDetails.productId = productId ?? undefined;
        }
      }

      this.messageStore.showSuccess({ key: "enterprise-account.update-subscription-success" });
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  isCompanyChecked(orgNo: string) {
    return this.checkedOrgNos.has(orgNo);
  }

  toggleCompany(orgNo: string) {
    if (this.isCompanyChecked(orgNo)) {
      this.checkedOrgNos.delete(orgNo);
    } else {
      this.checkedOrgNos.add(orgNo);
    }
  }

  *addFoundCompanies() {
    this.submitCompaniesResult = pending();
    try {
      const response: AddCompaniesToEnterpriseAccountResponse = yield this.http
        .put(`/enterprise-accounts/${this.id}/company`, {
          orgNos: this.filteredCheckedCompanies.map((c) => c.companyData.orgNo),
          completeOnboarding: false,
          adminUserIds: [],
        })
        .then((r) => r.data);
      this.submitCompaniesResult = ok();

      this.showSubmitCompaniesMessage(response);

      this.closeDialog();
      this.checkedOrgNos.clear();
      this.refresh();
    } catch (e) {
      this.submitCompaniesResult = failure();
    }
  }

  *purgeAccount() {
    this.purgeAccountResult = pending();
    try {
      yield this.http.post(`/enterprise-accounts/${this.id}/purge`, {
        orgNos: isOk(this.accountResult)
          ? this.accountResult.data.companyDetails.map((c) => c.orgNo)
          : [],
      });
      this.purgeAccountResult = ok();
      this.closeDialog();
      this.messageStore.showGeneralSuccess();
      this.dispatch([navigate(`${superadminUrl}/enterprise-accounts`)]);
    } catch (e) {
      this.purgeAccountResult = failure();
      this.messageStore.showGeneralError();
    }
  }

  private showSubmitCompaniesMessage(response: AddCompaniesToEnterpriseAccountResponse) {
    this.dispatch([
      showAlert(
        {
          key:
            response.notFound.length > 0
              ? "enterprise-account.added-companies-missing"
              : "enterprise-account.added-companies",
          params: {
            count: response.numberOfAddedCompanies,
            orgNos: response.notFound.join(", "),
          },
        },
        "info",
      ),
    ]);
  }

  selectAllCompanies() {
    this.checkedOrgNos.clear();
    this.filteredCompanies.forEach((c) => this.checkedOrgNos.add(c.companyData.orgNo));
  }

  selectNoCompanies() {
    this.checkedOrgNos.clear();
  }

  submitUpdateMultipleCompaniesDialog() {
    const model = this.updateMultipleCompaniesModel;
    if (!model) return;
    const update = [thunk(() => this.updateMultipleCompanies(model))];
    this.dispatch(
      model.downgradesToOwnershipRegister ? confirmDowngradeToOwnershipRegister(update) : update,
    );
  }

  *updateMultipleCompanies(model: UpdateMultipleCompaniesModel) {
    try {
      const isCreatingNewPortfolio =
        model.changeType.value === EnterpriseAccountChangeType.addToPortfolio &&
        model.addToPortfolioModel.value === NewPortfolioOption;

      if (isCreatingNewPortfolio) {
        yield createPortfolioWithCompanies(this.http, model);
      } else {
        yield this.http
          .post(`/enterprise-accounts/${this.id}/company`, model.prepareData())
          .then((r) => r.data);
      }

      this.closeDialog();

      this.messageStore.showSuccess(this.submitUpdateMultipleSuccessMessage);
      yield this.fetchEnterpriseAccount(this.id!);
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  get submitUpdateMultipleSuccessMessage(): TMessage {
    return this.updateMultipleCompaniesModel?.changeType.value ===
      EnterpriseAccountChangeType.addToPortfolio
      ? {
          key: "enterprise-account.add-to-portfolio-success",
          params: {
            count: this.updateMultipleCompaniesModel?.selectedOrgNos.length,
            name:
              this.companyData.find((c) =>
                this.updateMultipleCompaniesModel?.selectedOrgNos.some((o) => o === c.orgNo),
              )?.companyName ?? "",
            portfolioName: this.updateMultipleCompaniesModel?.portfolioNewNameModel.validatedValue,
          },
        }
      : { key: "enterprise-account.update-subscription-success" };
  }

  openConfirmRemoveDialog(companyDetails: AccountCompanyDetails) {
    this.showDialog = { type: "confirm-remove" };
    this.selectedCompanyDetails = companyDetails;
    this.offboardSelectedCompany = false;
  }

  setOffboardSelectedCompany(value: boolean) {
    this.offboardSelectedCompany = value;
  }

  *submitRemoveCompanyDialog() {
    const orgNo = this.selectedCompanyDetails!.orgNo;
    try {
      yield this.http
        .post(`/enterprise-accounts/${this.id}/company`, {
          orgNos: [orgNo],
          changeType: EnterpriseAccountChangeType.removeCompanies,
          offboard: this.offboardSelectedCompany,
        })
        .then((r) => r.data);

      this.closeDialog();
      this.refresh();
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  *addAccountAdmin(id: string) {
    this.addAccountAdminIdLoading = id;
    try {
      yield this.http.post(`/enterprise-accounts/${this.id}/admin`, {
        externalId: id,
      });
      if (isOk(this.accountResult)) {
        this.accountResult.data.account.accountAdminIds.push(id);
      }
      this.messageStore.showGeneralSuccess();
    } catch (e) {
      this.messageStore.showGeneralError();
    } finally {
      this.addAccountAdminIdLoading = undefined;
    }
  }

  *removeAccountAdmin(userId: string) {
    try {
      yield this.http.delete(`/enterprise-accounts/${this.id}/admin/${userId}`);
      this.refresh();
      this.messageStore.showGeneralSuccess();
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  openEditFeatureDialog(type: EnterpriseAccountFeatureType, feature?: EnterpriseAccountFeature) {
    const companyDetails = isOk(this.accountResult) ? this.accountResult.data.companyDetails : [];
    this.editFeatureModel = new EditFeatureModel(type, companyDetails, feature);
  }

  closeEditFeatureDialog() {
    this.editFeatureModel = undefined;
  }

  *submitEditFeatureDialog() {
    if (!this.editFeatureModel || !this.editFeatureModel.validate()) return;
    try {
      yield this.http.post(
        `/enterprise-accounts/${this.id}/features`,
        this.editFeatureModel.prepareData(),
      );
      this.closeEditFeatureDialog();
      this.fetchEnterpriseAccount(this.id!);
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  private customRoleName(roleId: string, fallback: string): string {
    if (!isOk(this.accountResult)) return fallback;
    const item = this.accountResult.data.customRoleItems.find(
      (r) => r.role.customRoleId === roleId,
    );
    return item ? getRoleNameByLanguage(item.role, this.language) : fallback;
  }

  usersRoles(user: EnterpriseAccountUser): TMessage | undefined {
    const { role } = user;
    if (role.type === "admin") return { key: "administrators" };
    if (role.type === "customRole")
      return { translated: this.customRoleName(role.roleId, role.roleName) };
    return undefined;
  }

  get userMenuOptions(): DropdownMenuProps {
    const button = { type: "button", variant: "secondary", label: { key: "invite-new" } } as const;
    const customRoleItems = isOk(this.accountResult)
      ? this.accountResult.data.customRoleItems.map((r) => ({
          type: "item" as const,
          label: { translated: this.customRoleName(r.role.customRoleId, r.role.customRoleName) },
          icon: "user-plus" as const,
          onSelect: () =>
            this.openInviteCustomRoleDialog({ customRoleItem: r, locale: this.language }),
        }))
      : [];
    return {
      button,
      items: [
        {
          type: "item" as const,
          label: { key: "administrators" },
          icon: "user-circle-gear",
          onSelect: this.openInviteAdminDialog,
        },
        ...customRoleItems,
      ],
    };
  }
}

export class EditFeatureModel {
  statusModel: RadioFieldModel<EnterpriseAccountFeatureStatus>;
  companies: OptionFieldModel<AccountCompanyDetails>;

  constructor(
    readonly type: EnterpriseAccountFeatureType,
    companyDetails: AccountCompanyDetails[],
    readonly feature?: EnterpriseAccountFeature,
  ) {
    this.statusModel = new RadioFieldModel({
      options: [
        EnterpriseAccountFeatureStatus.DISABLED,
        EnterpriseAccountFeatureStatus.ENABLED_FOR_ALL_COMPANIES,
        EnterpriseAccountFeatureStatus.ENABLED_FOR_SELECTED_COMPANIES,
      ],
      labelFn: (status) => ({ key: accountFeatureStatusLabels[status] }),
      value: feature?.status ?? EnterpriseAccountFeatureStatus.DISABLED,
      idFn: identity,
    });
    this.companies = new OptionFieldModel<AccountCompanyDetails>({
      options: sortBy(companyDetails, "companyName"),
      idFn: (company) => company.companyId,
      labelFn: (company) => ({ translated: company.companyName }),
      value: companyDetails.filter((company) =>
        feature?.selectedCompanyIds.includes(company.companyId),
      ),
      required: true,
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }

  get isPerCompany(): boolean {
    return this.statusModel.value === EnterpriseAccountFeatureStatus.ENABLED_FOR_SELECTED_COMPANIES;
  }

  get isSubmitDisabled(): boolean {
    return this.isPerCompany && this.companies.isNoneSelected;
  }

  prepareData() {
    return {
      type: this.type,
      status: this.statusModel.value,
      selectedCompanyIds: this.isPerCompany
        ? this.companies.selected.map((company) => company.companyId)
        : [],
    };
  }

  validate(): boolean {
    return !this.isPerCompany || this.companies.validate();
  }
}

export class AddCompaniesModel {
  readonly http: AxiosInstance;
  readonly orgNos = new StringFieldModel({ required: true });
  readonly searchField = new StringFieldModel();
  readonly subscriptionModel = new RadioFieldModel<ProductName | null>({
    value: null,
    options: [null, ...productNames.filter((name) => !isLegacyProduct(name))],
    labelFn: productNameLabel,
    idFn: (s) => (s ? s : "none"),
  });
  readonly completeOnboarding = new BooleanFieldModel();
  readonly accountAdminIds: string[];
  searchUserResult?: Result<UserByEmailOrSocialSecurityNumber[]>;
  fetchUserAccessResult?: Result;
  adminUsers?: OptionFieldModel<UserOption>;
  fetchPortfoliosResult?: Result<Portfolio[]>;
  portfolioModel: RadioFieldModel<Portfolio | typeof NewPortfolioOption | null>;
  newPortfolioName = new StringFieldModel({ required: true });
  createPortfolioResult?: Result<TMessage, TMessage>;
  checkAvailabilityResult?: Result<CheckAvailabilityResponse>;

  constructor(
    http: AxiosInstance,
    adminUsers: UserOption[],
    user: User | null,
    accountAdminIds: string[],
  ) {
    this.http = http;
    this.accountAdminIds = accountAdminIds;
    const currentUser: UserOption[] = user
      ? [
          {
            id: user.externalId,
            name: {
              key: "enterprise-account.current-user" as const,
              params: {
                name: formatName(user),
              },
            },
          },
        ]
      : [];

    const users = currentUser.concat(adminUsers.filter((u) => u.id !== user?.externalId));
    this.adminUsers =
      users.length > 0
        ? new OptionFieldModel({
            options: users,
            idFn: (value) => value.id,
            labelFn: (value) => value.name,
          })
        : undefined;

    this.portfolioModel = new RadioFieldModel<Portfolio | typeof NewPortfolioOption | null>({
      value: null,
      options: [null, NewPortfolioOption],
      idFn: (option) => (option ? (typeof option === "string" ? option : option.id) : "null"),
      labelFn: () => ({ translated: "" }),
    });

    makeAutoObservable(this, { http: false }, { autoBind: true });
  }

  validate() {
    return this.orgNos.validate();
  }

  *search() {
    if (!this.searchField.inputValue) return;

    this.searchUserResult = pending();
    try {
      const response: UserByEmailOrSocialSecurityNumber[] = yield this.http
        .get(`/superadmin/lookup-user/${this.searchField.inputValue}`)
        .then((response) => response.data);
      this.searchUserResult = ok(response);
    } catch (e) {
      this.searchUserResult = failure();
    }
  }

  *fetchUserCompanies(userId: string) {
    this.fetchUserAccessResult = pending();
    try {
      const access: UserAccess = yield this.http
        .get(`/superadmin/user/${userId}/access`)
        .then((response) => response.data);

      this.orgNos.setAndCommitInputValue(access.admin.map((c) => c.orgNo).join("\n"));
      this.fetchUserAccessResult = ok();
    } catch (e) {
      this.fetchUserAccessResult = failure();
    }
  }

  *fetchPortfolios() {
    this.fetchPortfoliosResult = pending();
    try {
      const response: Portfolio[] = yield this.http
        .get(`/enterprise-accounts/portfolios`)
        .then((response) => response.data);
      this.fetchPortfoliosResult = ok(response);

      this.portfolioModel.updateOptions([null, NewPortfolioOption, ...response]);
    } catch (e) {
      this.fetchPortfoliosResult = failure();
    }
  }

  *createNewPortfolio() {
    if (!this.newPortfolioName.validate()) return;

    this.createPortfolioResult = pending();
    try {
      const newPortfolio: Portfolio = yield this.http
        .post(`/enterprise-accounts/portfolios`, {
          portfolioName: this.newPortfolioName.validatedValue,
        })
        .then((response) => response.data);
      this.createPortfolioResult = ok({ key: "enterprise-account.create-portfolio-success" });

      if (this.portfolioModel) {
        this.portfolioModel.updateOptions([...this.portfolioModel.options, newPortfolio]);
        this.portfolioModel.setValue(newPortfolio);
      }
    } catch (e) {
      this.createPortfolioResult = failure({ key: "general-error" });
    }
  }

  closeNewPortfolioDialog() {
    if (this.portfolioModel) {
      this.portfolioModel.setValue(null);
    }
  }

  isAccountAdmin(id: string) {
    return this.accountAdminIds.includes(id);
  }

  *checkAvailability() {
    if (!this.orgNos.validate() || this.parsedOrgNos.length === 0) {
      this.checkAvailabilityResult = undefined;
      return;
    }

    this.checkAvailabilityResult = pending();
    try {
      const response: CheckAvailabilityResponse = yield this.http
        .post(`/enterprise-accounts/check-availability`, {
          orgNos: this.parsedOrgNos,
        })
        .then((response) => response.data);

      this.checkAvailabilityResult = ok(response);
    } catch (e) {
      this.checkAvailabilityResult = failure();
    }
  }

  get parsedOrgNos() {
    return this.orgNos.validatedValue
      .split(/[,\n ]+/)
      .map((s) => removeAllWhitespace(s))
      .filter((s) => !isBlank(s));
  }

  get isAvailabilityCheckOk() {
    return (
      isOk(this.checkAvailabilityResult) &&
      this.checkAvailabilityResult.data.invalidOrgNos.length === 0 &&
      this.checkAvailabilityResult.data.notFoundOrgNos.length === 0 &&
      this.checkAvailabilityResult.data.companiesInOtherAccount.length === 0
    );
  }
}

export type CheckAvailabilityResponse = {
  availableOrgNos: string[];
  invalidOrgNos: string[];
  notFoundOrgNos: string[];
  companiesInOtherAccount: EnterpriseAccountCompany[];
};

export type EnterpriseAccountCompany = {
  orgNo: string;
  companyName: string;
  enterpriseAccountName: string;
};

type UserOption = {
  id: string;
  name: TMessage;
};

export type SsoSettingsModel = {
  type: SsoType;
  domains: StringFieldModel[];
  addDomain: () => void;
  requireSsoForAllUsers: BooleanFieldModel;
  requireBankIdOnInitialSetup: BooleanFieldModel;
  validate: () => boolean;
};

export const createSSOSettingsModel = (
  type: SsoType,
  existing?: SsoProviderSettings,
  requireSsoForAllUsers?: boolean,
): SsoSettingsModel => {
  const domains = observable.array(
    (existing?.domains ?? [""]).map(
      (domain) => new StringFieldModel({ value: domain, validator: validateDomain }),
    ),
  );
  const addDomain = () => {
    domains.push(new StringFieldModel({ validator: validateDomain }));
  };
  return {
    type,
    domains,
    addDomain,
    requireSsoForAllUsers: new BooleanFieldModel({ value: requireSsoForAllUsers ?? false }),
    requireBankIdOnInitialSetup: new BooleanFieldModel({
      value: existing?.requireBankIdOnInitialSetup ?? true,
    }),
    validate: (): boolean => {
      const results = domains.map((domain) => {
        const hasAnotherDomain = domains.some(
          (otherDomain) => otherDomain !== domain && !isBlank(otherDomain.inputValue),
        );
        domain.setRequired(!hasAnotherDomain);
        return domain.validate();
      });
      return results.every(Boolean);
    },
  };
};

const createSharepointSettingsModel = (settings?: SharepointSettings): SharepointSettingsModel => {
  const siteID = new StringFieldModel({ value: settings?.siteId, required: true });
  const clientID = new StringFieldModel({ value: settings?.applicationId, required: true });
  const clientSecret = new StringFieldModel({ value: settings?.secret, required: true });
  const tenantID = new StringFieldModel({ value: settings?.tenantId, required: true });
  const validate = (): boolean => {
    const results = [siteID, clientID, clientSecret, tenantID].map((validator) =>
      validator.validate(),
    );
    return results.every(Boolean);
  };

  return {
    type: "sharepoint",
    siteID,
    clientID,
    clientSecret,
    tenantID,
    validate,
  };
};

const enterpriseAccountsTabs = [
  "companies",
  "account",
  "users",
  "content",
  "fields",
  "settings",
] as const;

export type EnterpriseAccountsTab = (typeof enterpriseAccountsTabs)[number];

export function isEnterpriseAccountsTab(tab?: string): tab is EnterpriseAccountsTab {
  return tab !== undefined && enterpriseAccountsTabs.includes(tab as EnterpriseAccountsTab);
}

export function visibleEnterpriseAccountsTabs(access: {
  hasTemplates: boolean;
  hasEntityData: boolean;
}): EnterpriseAccountsTab[] {
  return enterpriseAccountsTabs.filter((tab) => {
    switch (tab) {
      case "content":
        return access.hasTemplates;
      case "fields":
        return access.hasEntityData;
      default:
        return true;
    }
  });
}

const enterpriseAccountsTabLabels: { [key in EnterpriseAccountsTab]: TMessageKey } = {
  companies: "enterprise-account.companies",
  account: "enterprise-account.account",
  users: "enterprise-account.users",
  content: "enterprise-account.content",
  fields: "enterprise-account.fields",
  settings: "enterprise-account.account-settings",
};

const accountSettingsTabs = [
  "dashboards",
  "dashboardCards",
  "companyRoles",
  "integrations",
  "email",
  "dataProtection",
  "features",
  "superAdmin",
] as const;

export type AccountSettingsTab = (typeof accountSettingsTabs)[number];

export function isAccountSettingsTab(tab?: string): tab is AccountSettingsTab {
  return tab !== undefined && accountSettingsTabs.includes(tab as AccountSettingsTab);
}

export function visibleAccountSettingsTabs(access: {
  hasSuperAdminAccess: boolean;
}): AccountSettingsTab[] {
  return accountSettingsTabs.filter((tab) => tab !== "superAdmin" || access.hasSuperAdminAccess);
}

export function activeAccountSettingsTab(
  tab: string | undefined,
  visibleTabs: AccountSettingsTab[],
): AccountSettingsTab {
  return isAccountSettingsTab(tab) && visibleTabs.includes(tab) ? tab : visibleTabs[0]!;
}

const accountSettingsTabLabels: { [key in AccountSettingsTab]: TMessageKey } = {
  dashboards: "enterprise-account.dashboards",
  dashboardCards: "enterprise-account.dashboards.tab.cards",
  companyRoles: "enterprise-account.company-roles",
  integrations: "enterprise-account.integrations",
  email: "enterprise-account.account-settings.email",
  dataProtection: "enterprise-account.account-settings.data-protection",
  features: "enterprise-account.features",
  superAdmin: "enterprise-account.super-admin-settings",
};

const accountSettingsTabIcons: { [key in AccountSettingsTab]: IconName } = {
  dashboards: "tabs",
  dashboardCards: "squares-four",
  companyRoles: "user-circle-gear",
  integrations: "puzzle-piece",
  email: "bell",
  dataProtection: "lock",
  features: "plugs-connected",
  superAdmin: "detective",
};

const accountSettingsMenuGroups: { label: TMessageKey; tabs: AccountSettingsTab[] }[] = [
  { label: "enterprise-account.dashboards", tabs: ["dashboards", "dashboardCards"] },
];

export function accountSettingsMenuProps(
  visibleTabs: AccountSettingsTab[],
  activeTab: AccountSettingsTab,
  open: (tab: AccountSettingsTab) => void,
): SideMenuEntry[] {
  const tabProps = (tab: AccountSettingsTab): MenuLinkProps => ({
    icon: accountSettingsTabIcons[tab],
    text: { key: accountSettingsTabLabels[tab] },
    isActive: activeTab === tab,
    onActivate: () => open(tab),
  });
  return visibleTabs.flatMap((tab): SideMenuEntry[] => {
    const group = accountSettingsMenuGroups.find((g) => g.tabs.includes(tab));
    if (!group) return [tabProps(tab)];
    const tabs = group.tabs.filter((t) => visibleTabs.includes(t));
    if (tabs[0] !== tab) return [];
    return [{ text: { key: group.label }, items: tabs.map(tabProps) }];
  });
}

export type IntegrationSettingsModel = SharepointSettingsModel | SsoSettingsModel;

export type SharepointSettingsModel = {
  type: "sharepoint";
  siteID: StringFieldModel;
  clientID: StringFieldModel;
  clientSecret: StringFieldModel;
  tenantID: StringFieldModel;
  validate: () => boolean;
};

export type AccountSubscription = { productName: string; label: string };

export const OWNERSHIP_REGISTER_PRODUCT = "ownership_register";

export function subscriptionsWithQuota(
  data: EnterpriseAccountDetails,
  lang: Language,
): AccountSubscription[] {
  const withQuota = new Set(
    data.account.subscriptionLimits.filter((limit) => limit.limit > 0).map((it) => it.productId),
  );
  return offerableProducts(data.products)
    .filter((product) => withQuota.has(product.productId))
    .map((product) => toAccountSubscription(product, lang));
}

export function registerAccountSubscriptions(
  products: ProductDescription[],
  lang: Language,
): AccountSubscription[] {
  return registerSubscriptions.map((productName) => {
    const product = products.find((p) => p.productName === productName);
    return product ? toAccountSubscription(product, lang) : { productName, label: productName };
  });
}

function toAccountSubscription(product: ProductDescription, lang: Language): AccountSubscription {
  return {
    productName: product.productName,
    label: product.productLanguages.find((l) => l.language === lang)?.name ?? product.productName,
  };
}

export function formatAccountStatus(
  lang: Language,
  data: EnterpriseAccountDetails,
  productId: string,
) {
  const subscriptionCount = data.subscriptionStats.find(
    (s) => s.productId === productId,
  )?.subscriptionCount;
  const subscriptionLimit = data.account.subscriptionLimits.find(
    (s) => s.productId === productId,
  )?.limit;
  return (
    formatNumber(lang, subscriptionCount ?? 0) + " / " + formatNumber(lang, subscriptionLimit ?? 0)
  );
}

export type EnterpriseAccountDialog =
  | { type: "none" }
  | { type: "remove-all-companies" }
  | { type: "delete-account" }
  | { type: "confirm-remove" }
  | { type: "find-companies" }
  | { type: "update-companies" }
  | { type: "add-admin-access"; model: AddAdministratorAccessModel }
  | { type: "remove-admin-access"; model: RemoveAdministratorAccessModel }
  | { type: "grant-project-access"; user: EnterpriseAccountUser }
  | { type: "purge-account"; model: BooleanFieldModel }
  | { type: "invite-admin"; model: InviteNewAdminDialogStore }
  | { type: "invite-custom-role"; model: InviteUserToCustomRoleModel };

export function productNameLabel(productName: ProductName | null): TMessage {
  switch (productName) {
    case "shareholder_register":
      return { key: "enterprise-account.shareholder-register" };
    case "digital_company":
      return { key: "enterprise-account.digital-company" };
    case "ownership_register":
      return { key: "enterprise-account.ownership-register" };
    case "partnership_register":
      return { key: "enterprise-account.partnership-register" };
    case "foundation":
      return { key: "enterprise-account.foundation" };
    case "enterprise":
      return { translated: "Enterprise" };
    case "pro":
      return { translated: "Pro" };
    case "basic":
      return { translated: "Basic" };
    case null:
      return { key: "enterprise-account.no-subscription" };
  }
}

export function formatLink(link: string) {
  return link.startsWith("https://") ? link : "https://" + link;
}

type GrantProjectAccessResponse = {
  numberOfProjects: number;
};

type AddCompaniesToEnterpriseAccountResponse = {
  numberOfAddedCompanies: number;
  notFound: string[];
};

type RelatedCompany = {
  companyData: NamedOrganisationNumber;
  admins: UserItem[];
  productName: ProductName;
};

export type FindTripletexCustomerResponse = {
  customer?: TripletexCustomer;
  contact?: TripletexCustomerContact;
  accountManager?: TripletexEmployee;
};

export type TripletexCustomer = {
  id: number;
  name: string;
  invoiceEmail: string;
};

export type TripletexCustomerContact = {
  id: number;
  displayName: string;
};

export type TripletexEmployee = {
  email: string;
  displayName: string;
};

class UpdateMultipleCompaniesModel {
  companies: OptionFieldModel<AccountCompanyDetails>;
  changeType: RadioFieldModel<EnterpriseAccountChangeType>;
  subscriptionModel: RadioFieldModel<string>;
  productFilter: RadioFieldModel<string>;
  offboardModel: BooleanFieldModel;
  addToPortfolioModel: RadioFieldModel<string | undefined>;
  portfolioNewNameModel: StringFieldModel;
  portfolioConfig: UpdateAddToPortfolio;

  constructor(readonly account: EnterpriseAccountDetails) {
    const companyDetails: AccountCompanyDetails[] = account.companyDetails;
    this.changeType = new RadioFieldModel({
      value: EnterpriseAccountChangeType.subscription,
      options: [
        EnterpriseAccountChangeType.subscription,
        EnterpriseAccountChangeType.addToPortfolio,
        EnterpriseAccountChangeType.removeCompanies,
      ],
      idFn: (value) => value,
      labelFn: (value) => ({ key: `enterprise-account.change-type.${value}` }),
    });
    this.subscriptionModel = new RadioFieldModel<string>({
      value: "none",
      options: ["none", ...offerableProducts(account.products).map((p) => p.productId)],
      idFn: (s) => s,
      labelFn: (s) => ({ translated: s }),
    });

    this.productFilter = new RadioFieldModel<string>({
      value: "all",
      options: ["all", ...uniq(compact(companyDetails.map((d) => d.productId)))],
      idFn: (value) => value,
      labelFn: (s) => ({ translated: s }),
    });

    this.offboardModel = new BooleanFieldModel();

    const existingPortfolioIds = [...new Set(compact(companyDetails.map((s) => s.portfolioId)))];
    this.portfolioConfig = {
      createNewPortfolio: NewPortfolioOption,
      existingPortfolioNames: compact(companyDetails.map((s) => s.portfolioName)),
    };
    this.addToPortfolioModel = new RadioFieldModel<string | undefined>({
      value: undefined,
      options: [...existingPortfolioIds, NewPortfolioOption],
      idFn: (item) => item ?? NewPortfolioOption,
      labelFn: (item) => this.addToPortfolioLabelFn(item, companyDetails),
    });

    this.portfolioNewNameModel = new StringFieldModel({ required: true });
    this.companies = new OptionFieldModel({
      options: () => sortBy(this.filteredCompanies, "companyName"),
      idFn: (value) => value.orgNo,
      labelFn: (value) => ({ translated: value.companyName }),
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  addToPortfolioLabelFn(item?: string, companyDetails?: AccountCompanyDetails[]): TMessage {
    if (item === NewPortfolioOption) return { key: "enterprise-account.create-portfolio" };
    const name = companyDetails?.find((c) => c.portfolioId === item)?.portfolioName;
    return { translated: name ?? "" };
  }

  productName(productId: string | undefined, lang: Language) {
    return this.account.products
      .find((p) => p.productId === productId)
      ?.productLanguages.find((l) => l.language === lang)?.name;
  }

  get filteredCompanies() {
    const productFilter = this.productFilter.value;
    const changeType = this.changeType.value;
    return this.account.companyDetails.filter((company) => {
      const matchesProduct =
        !productFilter || productFilter === "all" || company.productId === productFilter;

      const matchesChangeType =
        changeType !== EnterpriseAccountChangeType.addToPortfolio || !company.portfolioId;

      return matchesProduct && matchesChangeType;
    });
  }

  get isSubmitDisabled() {
    return (
      this.companies.selected.length === 0 ||
      this.hasAddPortfolioError ||
      this.selectedSubscriptionRestriction !== undefined
    );
  }

  get selectedSubscriptionRestriction(): TMessage | undefined {
    if (this.changeType.value !== EnterpriseAccountChangeType.subscription) return;
    return subscriptionRestriction(
      this.account.products,
      this.subscriptionModel.value,
      this.companies.selected,
    );
  }

  get downgradesToOwnershipRegister(): boolean {
    return (
      this.changeType.value === EnterpriseAccountChangeType.subscription &&
      downgradesToOwnershipRegister(
        this.account.products,
        this.companies.selected,
        this.subscriptionModel.value,
      )
    );
  }

  get hasAddPortfolioError(): boolean {
    if (this.changeType.value !== EnterpriseAccountChangeType.addToPortfolio) return false;
    const portfolio = this.addToPortfolioModel.value;
    return (
      portfolio === undefined ||
      (portfolio === NewPortfolioOption && this.portfolioNewNameModel.isEmpty)
    );
  }

  get selectedOrgNos() {
    return this.companies.selected.map((c) => c.orgNo);
  }

  prepareData(): UpdateCompaniesRequest {
    const isAddToPortfolio = this.changeType.value === EnterpriseAccountChangeType.addToPortfolio;
    const portfolioValue = this.addToPortfolioModel.value;
    return {
      orgNos: this.selectedOrgNos,
      changeType: this.changeType.value,
      productId: this.subscriptionModel.value === "none" ? null : this.subscriptionModel.value,
      offboard: this.offboardModel.value ?? false,
      portfolioName:
        isAddToPortfolio && portfolioValue === NewPortfolioOption
          ? this.portfolioNewNameModel.validatedValue
          : undefined,
      portfolioId:
        isAddToPortfolio && portfolioValue !== NewPortfolioOption ? portfolioValue : undefined,
    };
  }

  selectAll() {
    this.companies.setSelected(this.filteredCompanies);
  }

  unselectAll() {
    this.filteredCompanies.forEach((c) => this.companies.unselect(c));
  }
}

export enum EnterpriseAccountChangeType {
  subscription = "subscription",
  addToPortfolio = "addToPortfolio",
  removeCompanies = "removeCompanies",
}

export enum AccessRequestRecipients {
  ALL_ADMINS = "ALL_ADMINS",
  ACCOUNT_ADMINS = "ACCOUNT_ADMINS",
}

export enum NationalIdMaskingPolicy {
  FULLY_MASKED = "FULLY_MASKED",
  ACCOUNT_ADMINS = "ACCOUNT_ADMINS",
  ALL_ADMINS = "ALL_ADMINS",
}

export const NewPortfolioOption = "NewPortfolio";

export type UpdateAddToPortfolio = {
  createNewPortfolio: string;
  defaultLabel?: string;
  existingPortfolioNames: string[];
};

export class AddAdministratorAccessModel {
  selectedAdmin: EnterpriseAccountUser;
  allAvailableCompanies: AccountCompanyDetails[];

  availableCompanies: OptionFieldModel<CompanyItem>;

  constructor(
    selectedAdmin: EnterpriseAccountUser,
    allAvailableCompanies: AccountCompanyDetails[],
  ) {
    this.selectedAdmin = selectedAdmin;
    this.allAvailableCompanies = allAvailableCompanies;
    this.availableCompanies = new OptionFieldModel<CompanyItem>({
      options: () => sortBy(this.availableCompaniesOptions, "companyName"),
      idFn: (item) => item.organisationNumber,
      labelFn: (item) => ({ translated: item.companyName }),
    });
    makeAutoObservable(this);
  }

  get availableCompaniesOptions(): CompanyItem[] {
    return this.allAvailableCompanies
      .filter((company) =>
        this.selectedAdmin.companies.every(
          (adminCompany) => company.orgNo !== adminCompany.organisationNumber,
        ),
      )
      .map((company) => ({ companyName: company.companyName, organisationNumber: company.orgNo }));
  }

  get isSubmitDisabled(): boolean {
    return this.availableCompanies.isNoneSelected;
  }
}

export class RemoveAdministratorAccessModel {
  selectedAdmin: EnterpriseAccountUser;
  availableCompanies: OptionFieldModel<CompanyItem>;

  constructor(selectedAdmin: EnterpriseAccountUser) {
    this.selectedAdmin = selectedAdmin;
    this.availableCompanies = new OptionFieldModel<CompanyItem>({
      options: selectedAdmin.companies,
      idFn: (item) => item.organisationNumber,
      labelFn: (item) => ({ translated: item.companyName }),
    });
    makeAutoObservable(this);
  }

  get isSubmitDisabled(): boolean {
    return this.availableCompanies.isNoneSelected;
  }
}

function createPortfolioWithCompanies(
  http: AxiosInstance,
  model: UpdateMultipleCompaniesModel,
): Promise<void> {
  return http
    .post("/enterprise-accounts/portfolios", {
      portfolioName: model.portfolioNewNameModel.validatedValue,
      orgNos: model.selectedOrgNos,
    })
    .then(() => undefined);
}

type UpdateCompaniesRequest = {
  orgNos: string[];
  changeType: EnterpriseAccountChangeType;
  productId: string | null;
  offboard: boolean;
  portfolioName?: string;
  portfolioId?: string;
};
